import { Component, inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, firstValueFrom, of, Subscription, switchMap, take, timer } from 'rxjs';
import { CellClickedEvent, ColDef, ColGroupDef } from 'ag-grid-community';
import Swal from 'sweetalert2';
import { EfateCakeRunApiService } from 'src/app/shared/services/efate-cake/efate-cake-run.api.service';
import { EfateSetupApiService } from 'src/app/shared/services/echo/efate-setup.api.service';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { Constants } from 'src/app/shared/utils/constants';
import { EFReportsGrid } from './ef-reports-grid';

@Component({
  selector: 'app-ef-reports',
  templateUrl: './ef-reports.component.html',
  styleUrls: ['./ef-reports.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class EFReportsComponent implements OnDestroy {
  private readonly efateCakeRunApiService = inject(EfateCakeRunApiService);
  private readonly efateSetupApiService = inject(EfateSetupApiService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly statusIntervalMs = 5000;
  private readonly maxConsecutiveErrors = 5;
  private statusSubscription?: Subscription;
  private runId?: string;
  private previewRunId?: string;
  private previewObjectUrl?: string;

  previewVisible: boolean = false;
  previewLoading: boolean = false;
  previewDataset: string = '';
  previewUrl?: SafeResourceUrl;
  readonly downloadButtonLabel = EFateConstants.REPORTS_GRID.DOWNLOAD_BUTTON_LABEL;
  readonly previewTitle = EFateConstants.REPORTS_GRID.PREVIEW_TITLE;

  isLoading: boolean = false;
  isLoadingSaved: boolean = false;
  isReady: boolean = false;
  showTimeNote: boolean = false;
  efateSetupPk?: number;
  statusName: string = '';
  badgeStyle: string = '';
  result: any;

  columnDefs: (ColDef | ColGroupDef)[] = [];
  rowData: any[] = [];
  readonly runButtonLabel = EFateConstants.REPORTS_GRID.RUN_BUTTON_LABEL;
  readonly runStatusLabel = EFateConstants.REPORTS_GRID.RUN_STATUS_LABEL;
  readonly timeNoteLabel = EFateConstants.REPORTS_GRID.TIME_NOTE_LABEL;
  readonly timeNotes = EFateConstants.TIME_UNIT_OPTIONS.map(option => ({
    marker: EFateConstants.REPORTS_GRID.TIME_UNIT_MARKERS[option.value],
    label: option.label,
  }));
  readonly defaultColDef: ColDef = { editable: false, resizable: true, sortable: true };
  readonly rowHeight = EFateConstants.REPORTS_GRID.ROW_HEIGHT;
  readonly headerHeight = EFateConstants.REPORTS_GRID.HEADER_HEIGHT;
  readonly groupHeaderHeight = EFateConstants.REPORTS_GRID.GROUP_HEADER_HEIGHT;

  async runCake(efateSetupPk?: number): Promise<void> {
    if (!this.prepare(efateSetupPk, true)) {
      return;
    }

    this.setStatus('Running');

    let runId: string | undefined;
    try {
      const job = await firstValueFrom(this.efateCakeRunApiService.startCAKE(efateSetupPk!).pipe(take(1)));
      runId = job?.runId ?? job?.RunId;
      this.applyRunningDatasets(job?.datasets ?? job?.Datasets, job?.existingDatasets ?? job?.ExistingDatasets);
    } catch (error) {
      console.warn('Error starting CAKE', error);
      this.isLoading = false;
      this.setStatus('Failed');
      return;
    }

    this.monitorCake(efateSetupPk!, runId);
  }

  showLastRun(efateSetupPk?: number): void {
    if (this.prepare(efateSetupPk)) {
      this.isLoadingSaved = true;
      this.monitorCake(efateSetupPk!);
    }
  }

  async hasSavedRun(efateSetupPk: number): Promise<boolean> {
    try {
      const status = await firstValueFrom(this.efateCakeRunApiService.getCAKEStatus(efateSetupPk).pipe(take(1)));
      return !!status?.status && status.status !== 'NotStarted';
    } catch {
      return false;
    }
  }

  async onRunClick(): Promise<void> {
    if (this.rowData.length > 0 && !(await this.confirmRerun())) {
      return;
    }

    this.runCake(this.efateSetupPk);
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.releasePreview();
  }

  onCellClicked(event: CellClickedEvent): void {
    if (event.colDef.field !== EFReportsGrid.PREVIEW_FIELD || !event.data || EFReportsGrid.isRunning(event.data)) {
      return;
    }

    this.openPreview(String(event.data.dataset), event.data.runId || undefined);
  }

  async openPreview(dataset: string, runId?: string): Promise<void> {
    if (!this.efateSetupPk) {
      return;
    }

    this.releasePreview();
    this.previewDataset = dataset;
    this.previewRunId = runId ?? this.runId;
    this.previewVisible = true;
    this.previewLoading = true;

    try {
      const html = await firstValueFrom(this.efateCakeRunApiService.getReportPreview(this.efateSetupPk, dataset, this.previewRunId).pipe(take(1)));
      this.previewObjectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewObjectUrl);
    } catch (error) {
      console.warn('Error loading CAKE report preview', error);
      this.closePreview();
      Swal.fire({ text: EFateConstants.REPORTS_GRID.REPORT_NOT_FOUND_TEXT, icon: 'warning' });
    } finally {
      this.previewLoading = false;
    }
  }

  async downloadReport(): Promise<void> {
    if (!this.efateSetupPk || !this.previewDataset) {
      return;
    }

    try {
      const blob = await firstValueFrom(this.efateCakeRunApiService.downloadReport(this.efateSetupPk, this.previewDataset, this.previewRunId).pipe(take(1)));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.previewDataset}${EFateConstants.REPORTS_GRID.REPORT_FILE_EXTENSION}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Error downloading CAKE report', error);
      Swal.fire({ text: EFateConstants.REPORTS_GRID.REPORT_NOT_FOUND_TEXT, icon: 'warning' });
    }
  }

  closePreview(): void {
    this.previewVisible = false;
    this.releasePreview();
  }

  private releasePreview(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
    }
    this.previewObjectUrl = undefined;
    this.previewUrl = undefined;
  }

  private async confirmRerun(): Promise<boolean> {
    const C = EFateConstants.REPORTS_GRID;
    const result = await Swal.fire({
      text: C.RERUN_CONFIRM_TEXT,
      showCancelButton: true,
      confirmButtonColor: C.CONFIRM_BUTTON_COLOR,
      confirmButtonText: C.CONFIRM_BUTTON_TEXT,
      cancelButtonText: C.CANCEL_BUTTON_TEXT,
      icon: 'warning',
    });

    return !!result.value;
  }

  private prepare(efateSetupPk?: number, keepGrid: boolean = false): boolean {
    this.stopMonitoring();
    this.isReady = false;
    this.isLoadingSaved = false;
    this.result = undefined;
    this.statusName = '';
    this.badgeStyle = '';

    const isValid = !!efateSetupPk && efateSetupPk > 0;
    if (!keepGrid || !isValid) {
      this.columnDefs = [];
      this.rowData = [];
      this.showTimeNote = false;
    }

    if (!isValid) {
      this.isLoading = false;
      return false;
    }

    this.isLoading = true;
    this.efateSetupPk = efateSetupPk;
    if (!this.columnDefs.length) {
      this.columnDefs = EFReportsGrid.build([]).columnDefs;
    }
    return true;
  }

  private applyRunningDatasets(datasets?: string[], existingDatasets?: string[]): void {
    const running = new Set((datasets ?? []).map(name => name.toLowerCase()));
    const existing = existingDatasets?.length ? new Set(existingDatasets.map(name => name.toLowerCase())) : undefined;

    const keptRows = this.rowData.filter(row => {
      const name = String(row.dataset).toLowerCase();
      return !running.has(name) && (!existing || existing.has(name));
    });

    if (!keptRows.length) {
      this.columnDefs = EFReportsGrid.build([]).columnDefs;
      this.rowData = [];
      this.showTimeNote = false;
      return;
    }

    const inProgressRows = (datasets ?? []).map(dataset => ({
      dataset,
      parentModel: Constants.PROJECT_STATUS.IN_PROGRESS,
      extraSolver: '',
    }));
    this.rowData = [...keptRows, ...inProgressRows];
  }

  private monitorCake(efateSetupPk: number, runId?: string): void {
    let consecutiveErrors = 0;

    this.statusSubscription = timer(0, this.statusIntervalMs).pipe(
      switchMap(() => this.efateCakeRunApiService.getCAKEStatus(efateSetupPk, runId).pipe(
        take(1),
        catchError((error) => {
          console.warn('Error getting CAKE status', error);
          return of(null);
        })
      ))
    ).subscribe(async (status: any) => {
      if (!status) {
        if (++consecutiveErrors >= this.maxConsecutiveErrors) {
          this.stopMonitoring();
          this.isLoading = false;
          this.isLoadingSaved = false;
          this.setStatus('Failed');
        }
        return;
      }

      consecutiveErrors = 0;

      if (status.status === 'Running') {
        this.isLoadingSaved = false;
        return;
      }

      this.stopMonitoring();

      // Loading stays on until the grid has its data, otherwise the empty grid flashes first.
      try {
        if (status.status === 'Completed') {
          this.result = status.result;
          this.runId = status.runId ?? status.RunId ?? runId;
          await this.showResult(efateSetupPk);
        } else {
          console.warn('Error running CAKE', status.error ?? status.status);
        }
      } catch (error) {
        console.warn('Error showing CAKE reports', error);
      } finally {
        this.isLoading = false;
        this.isLoadingSaved = false;
        this.setStatus(status.status);
      }
    });
  }

  private async showResult(efateSetupPk: number): Promise<void> {
    const timeUnits = await this.getTimeUnits(efateSetupPk);
    const grid = EFReportsGrid.build(this.result?.summaries ?? this.result?.Summaries, timeUnits);
    this.columnDefs = grid.columnDefs;
    this.rowData = grid.rowData;
    this.showTimeNote = grid.showTimeNote;
    this.isReady = true;
  }

  private async getTimeUnits(efateSetupPk: number): Promise<Record<string, string>> {
    try {
      const [dataSets, studyOptions] = await Promise.all([
        firstValueFrom(this.efateSetupApiService.getEfateCakeDataSetsBySetupPk(efateSetupPk).pipe(take(1))),
        firstValueFrom(this.efateSetupApiService.getEfateStudyOptionsBySetupPk(efateSetupPk).pipe(take(1))),
      ]);

      const unitByDatasetPk = new Map((studyOptions ?? []).map(options => [options.datasetPk, options.timeEnteredIn]));
      const timeUnits: Record<string, string> = {};
      (dataSets ?? []).forEach(dataSet => {
        const unit = unitByDatasetPk.get(dataSet.efateCakeDataSetPk ?? 0);
        if (unit) {
          timeUnits[dataSet.dataSetName.toLowerCase()] = unit;
        }
      });
      return timeUnits;
    } catch (error) {
      console.warn('Error loading CAKE time units', error);
      return {};
    }
  }

  private setStatus(status: string): void {
    switch (status) {
      case 'Running':
        this.statusName = Constants.PROJECT_STATUS.IN_PROGRESS;
        this.badgeStyle = Constants.BADGE_STYLE.IN_PROGRESS;
        break;
      case 'Completed':
        this.statusName = Constants.PROJECT_STATUS.SUCCESSFUL;
        this.badgeStyle = Constants.BADGE_STYLE.SUCCESSFUL;
        break;
      case 'Failed':
        this.statusName = Constants.PROJECT_STATUS.FAILED;
        this.badgeStyle = Constants.BADGE_STYLE.FAILED;
        break;
      default:
        this.statusName = Constants.PROJECT_STATUS.NOT_STARTED;
        this.badgeStyle = '';
    }
  }

  private stopMonitoring(): void {
    this.statusSubscription?.unsubscribe();
    this.statusSubscription = undefined;
  }
}